import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Camera, Plus, Trash2, Sparkles, X } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import BottomSheet from '../components/BottomSheet';
import PlantMarker from '../components/PlantMarker';
import PlantBottomSheet from '../components/PlantBottomSheet';
import TapToPlaceOverlay from '../components/TapToPlaceOverlay';
import SeasonSwitcher from '../components/SeasonSwitcher';
import { zonesApi, markersApi, plantsApi, photosApi, aiApi, pickZonePhoto } from '../services/gardenApi';
import { convertHeicToJpeg, isHeicFile } from '../../utils/convertHeic';
import { identifyPlantFromImageFile, mapPlantNetMatches, PLANT_IDENTIFY_ORGANS } from '../../services/plantIdentifyApi';
import { GARDEN_SUN_EXPOSURE_OPTIONS } from '../constants';
import { useGardenPageTitle } from '../useGardenPageTitle';
import { displayBotanicalSubtitle, displayPrimaryPlantName } from '../utils/plantDisplayName';

function currentSeasonGuess() {
  const m = new Date().getMonth(); // 0=Jan
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'fall';
  return 'winter';
}

export default function ZoneDetailScreen() {
  const { zoneId } = useParams();
  const navigate = useNavigate();

  const [zone, setZone] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentSeason, setCurrentSeason] = useState(currentSeasonGuess());

  const [placementMode, setPlacementMode] = useState(false);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [choosePlantOpen, setChoosePlantOpen] = useState(false);
  /** Chooser: saved vs new before map placement */
  const [placementEntryOpen, setPlacementEntryOpen] = useState(false);
  const [placementEntryTab, setPlacementEntryTab] = useState('menu'); // 'menu' | 'library' | 'new'
  const [libraryPlantIdForPlacement, setLibraryPlantIdForPlacement] = useState(null);
  /** After "Add new" + form, map tap shows a short confirm instead of the full form */
  const [mapPlacementFromPreForm, setMapPlacementFromPreForm] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [draggingMarkerId, setDraggingMarkerId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [siteSheetOpen, setSiteSheetOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({ sun: '', notes: '' });
  const [siteSaving, setSiteSaving] = useState(false);

  useGardenPageTitle(!zone || loading ? 'Area' : zone.name, 'Area');

  const mapContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [zoneRes, plantsRes, markersRes] = await Promise.all([
        zonesApi.get(zoneId),
        plantsApi.list(),
        markersApi.list(zoneId),
      ]);
      setZone(zoneRes.zone);
      setPlants(plantsRes.plants || []);
      setMarkers(markersRes.markers || []);
      if (zoneRes.zone?.default_season) setCurrentSeason(zoneRes.zone.default_season);
    } catch (e) {
      setError(e.message || 'Failed to load this area');
    } finally {
      setLoading(false);
    }
  }, [zoneId]);

  useEffect(() => { load(); }, [load]);

  const hasPhoto = useMemo(() => ({
    spring: !!zone?.photo_spring_url,
    summer: !!zone?.photo_summer_url,
    fall:   !!zone?.photo_fall_url,
    winter: !!zone?.photo_winter_url,
  }), [zone]);

  const displayed = useMemo(() => pickZonePhoto(zone, currentSeason), [zone, currentSeason]);

  const handleUploadPhoto = async (file) => {
    if (!zone) return;
    setUploading(true);
    try {
      if (isHeicFile(file)) {
        file = await convertHeicToJpeg(file);
      }
      const r = await zonesApi.uploadSeasonPhoto(zone.id, currentSeason, file);
      setZone(r.zone);
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveSeasonPhoto = async () => {
    if (!zone) return;
    const ok = window.confirm(`Remove the ${currentSeason} photo for this area?`);
    if (!ok) return;
    try {
      const r = await zonesApi.removeSeasonPhoto(zone.id, currentSeason);
      setZone(r.zone);
    } catch (e) {
      alert(e.message || 'Could not remove photo');
    }
  };

  const pointFromEvent = (e) => {
    const rect = mapContainerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    return { xPercent: Math.max(0, Math.min(100, x)), yPercent: Math.max(0, Math.min(100, y)) };
  };

  const handleMapTap = async (e) => {
    if (draggingMarkerId) return;
    if (!placementMode) return; // require explicit flow to enter placement
    const pt = pointFromEvent(e);
    if (!pt) return;
    if (libraryPlantIdForPlacement) {
      const pid = libraryPlantIdForPlacement;
      setLibraryPlantIdForPlacement(null);
      setPlacementMode(false);
      await placeMarkerForPlant(pid, null, pt);
      return;
    }
    setPendingPoint(pt);
    setPlacementMode(false);
    setChoosePlantOpen(true);
  };

  const cancelPlacement = () => {
    setPlacementMode(false);
    setPendingPoint(null);
    setLibraryPlantIdForPlacement(null);
    setMapPlacementFromPreForm(false);
  };

  const placeMarkerForPlant = async (plantId, label, pointOverride = null) => {
    const pt = pointOverride ?? pendingPoint;
    if (!zone || !pt) return;
    try {
      const r = await markersApi.create(zone.id, {
        garden_plant_id: plantId,
        x_percent: pt.xPercent,
        y_percent: pt.yPercent,
        label: label || null,
      });
      setMarkers((prev) => [...prev, r.marker]);
    } catch (e) {
      alert(e.message || 'Could not place marker');
    } finally {
      setPendingPoint(null);
      setChoosePlantOpen(false);
      setMapPlacementFromPreForm(false);
    }
  };

  const [newPlantName, setNewPlantName] = useState('');
  const [quickNameMode, setQuickNameMode] = useState('common'); // 'common' | 'scientific'
  const [quickAddBusy, setQuickAddBusy] = useState(false);
  const [quickPhotoFile, setQuickPhotoFile] = useState(null);
  const [quickPhotoPreview, setQuickPhotoPreview] = useState(null);
  const quickPhotoInputRef = useRef(null);
  const [quickIdentifyOrgan, setQuickIdentifyOrgan] = useState('leaf');
  const [quickIdentifyBusy, setQuickIdentifyBusy] = useState(false);
  const [quickIdentifyMessage, setQuickIdentifyMessage] = useState(null);
  const [toast, setToast] = useState(null); // { plantId, plantName, aiFilled?: number, photoUploaded?: bool }

  // Revoke the object URL preview when we no longer need it to avoid leaks.
  useEffect(() => {
    if (!quickPhotoPreview) return undefined;
    return () => URL.revokeObjectURL(quickPhotoPreview);
  }, [quickPhotoPreview]);

  const handleQuickPhotoPicked = async (e) => {
    let f = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!f) return;
    if (isHeicFile(f)) {
      try {
        f = await convertHeicToJpeg(f);
      } catch (err) {
        alert(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }
    setQuickPhotoFile(f);
    setQuickPhotoPreview(URL.createObjectURL(f));
  };

  const clearQuickPhoto = () => {
    setQuickPhotoFile(null);
    setQuickPhotoPreview(null);
  };

  const resetQuickAdd = () => {
    setNewPlantName('');
    setQuickNameMode('common');
    setQuickIdentifyMessage(null);
    clearQuickPhoto();
  };

  useEffect(() => {
    if (!choosePlantOpen) return;
    setQuickIdentifyMessage(null);
  }, [choosePlantOpen]);

  async function buildQuickCreateBody(rawName, mode) {
    const trimmed = rawName.trim();
    if (mode === 'common') {
      return { common_name: trimmed };
    }
    try {
      const ai = await aiApi.plantLookup({ botanical_name: trimmed });
      const p = ai.plant || {};
      const cn = (p.common_name || '').trim();
      const bn = (p.botanical_name || '').trim();
      if (cn || bn) {
        return {
          common_name: cn || trimmed,
          botanical_name: bn || trimmed,
        };
      }
    } catch (_) {
      /* use fallback below */
    }
    return { common_name: trimmed, botanical_name: trimmed };
  }

  const suggestNameFromPhoto = async () => {
    if (!quickPhotoFile || quickIdentifyBusy) return;
    setQuickIdentifyMessage(null);
    setQuickIdentifyBusy(true);
    try {
      const idData = await identifyPlantFromImageFile(quickPhotoFile, quickIdentifyOrgan);
      const matches = mapPlantNetMatches(idData);
      const top = matches[0];
      if (!top || (!top.commonName && !top.scientificName)) {
        setQuickIdentifyMessage(
          'No confident match — try another photo or change the plant part (leaf / flower).',
        );
        return;
      }
      const label = (top.commonName || top.scientificName).trim();
      setNewPlantName(label);
      setQuickIdentifyMessage(
        `Suggested: ${top.scientificName || label}` +
          (top.commonName ? ` (${top.commonName})` : '') +
          '. Edit if needed.',
      );
    } catch (e) {
      setQuickIdentifyMessage(
        e?.status === 429
          ? (e.message || 'Daily photo identify limit reached — try again tomorrow.')
          : (e.message || 'Could not identify from photo.'),
      );
    } finally {
      setQuickIdentifyBusy(false);
    }
  };

  // Auto-dismiss the toast after ~6s.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const createPlantAndPlace = async ({ withAI = false } = {}) => {
    const name = newPlantName.trim();
    if (!name || quickAddBusy) return;
    setQuickAddBusy(true);
    const photoFile = quickPhotoFile;
    try {
      const createBody = await buildQuickCreateBody(name, quickNameMode);
      const r = await plantsApi.create(createBody);
      const newPlant = r.plant;
      setPlants((prev) => [newPlant, ...prev]);
      await placeMarkerForPlant(newPlant.id);

      // Attach the quick-add photo to the new plant's current-season slot.
      // A failed upload shouldn't lose the plant itself — log + continue.
      let photoUploaded = false;
      if (photoFile) {
        try {
          await photosApi.upload(newPlant.id, photoFile, { season: currentSeason });
          photoUploaded = true;
          const [mRes, plantsRes] = await Promise.all([
            markersApi.list(zone.id),
            plantsApi.list(),
          ]);
          setMarkers(mRes.markers || []);
          setPlants(plantsRes.plants || []);
        } catch (photoErr) {
          console.warn('[garden] quick-add photo upload failed:', photoErr);
        }
      }

      resetQuickAdd();
      setMapPlacementFromPreForm(false);

      let aiFilledCount = 0;
      const displayName = newPlant.common_name || name;
      if (withAI) {
        try {
          const ai = await aiApi.plantLookup({
            common_name: (newPlant.common_name || '').trim(),
            botanical_name: (newPlant.botanical_name || '').trim() || undefined,
          });
          const fields = ai.plant || {};
          if (Object.keys(fields).length > 0) {
            await plantsApi.patch(newPlant.id, fields);
            aiFilledCount = Object.keys(fields).length;
          }
        } catch (aiErr) {
          // Non-fatal: keep the plant + marker, surface a softer message in the toast.
          console.warn('[garden] AI fill failed for quick-add:', aiErr);
          let shortMsg;
          switch (aiErr.code) {
            case 'rate_limited':    shortMsg = 'AI is busy \u2014 try again soon'; break;
            case 'not_found':       shortMsg = "AI couldn't identify that plant"; break;
            case 'too_generic':     shortMsg = 'Name was too general for AI'; break;
            case 'not_configured':  shortMsg = 'AI not configured on the server'; break;
            case 'ai_unavailable':  shortMsg = 'AI temporarily unavailable'; break;
            default:                shortMsg = 'AI lookup failed';
          }
          setToast({
            plantId: newPlant.id,
            plantName: displayName,
            aiFilled: 0,
            aiError: shortMsg,
            photoUploaded,
          });
          return;
        }
      }
      setToast({ plantId: newPlant.id, plantName: displayName, aiFilled: aiFilledCount, photoUploaded });
    } catch (e) {
      alert(e.message || 'Could not add plant');
    } finally {
      setQuickAddBusy(false);
    }
  };

  // Long-press drag to reposition
  const onMarkerLongPressStart = (e, marker) => { setDraggingMarkerId(marker.id); };
  const markersRef = useRef(markers);
  useEffect(() => { markersRef.current = markers; }, [markers]);

  useEffect(() => {
    if (!draggingMarkerId) return;
    const onMove = (e) => {
      const pt = pointFromEvent(e);
      if (!pt) return;
      setMarkers((prev) => prev.map((m) =>
        m.id === draggingMarkerId ? { ...m, x_percent: pt.xPercent, y_percent: pt.yPercent } : m
      ));
    };
    const onUp = async () => {
      const id = draggingMarkerId;
      const current = markersRef.current.find((m) => m.id === id);
      setDraggingMarkerId(null);
      if (current) {
        try {
          await markersApi.patch(id, { x_percent: current.x_percent, y_percent: current.y_percent });
        } catch (e) {
          if (zone) {
            const mRes = await markersApi.list(zone.id);
            setMarkers(mRes.markers);
          }
        }
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [draggingMarkerId, zone]);

  const removeMarkerFromMap = async (marker) => {
    if (!marker) return;
    const ok = window.confirm('Remove this marker from the area? The plant stays in your library.');
    if (!ok) return;
    try {
      await markersApi.remove(marker.id);
      setMarkers((prev) => prev.filter((m) => m.id !== marker.id));
      setActiveMarker(null);
    } catch (e) {
      alert(e.message || 'Could not remove marker');
    }
  };

  const handleDeleteZone = async () => {
    if (!zone) return;
    const ok = window.confirm(`Delete "${zone.name}"? All markers in this area will be removed. Plants stay in your library.`);
    if (!ok) return;
    try {
      await zonesApi.remove(zone.id);
      navigate('/garden');
    } catch (e) {
      alert(e.message || 'Could not delete area');
    }
  };

  const openSiteEdit = () => {
    if (!zone) return;
    setSiteForm({ sun: zone.sun_exposure || '', notes: zone.site_notes || '' });
    setSiteSheetOpen(true);
  };

  const saveSiteDetails = async () => {
    if (!zone) return;
    setSiteSaving(true);
    try {
      const r = await zonesApi.patch(zone.id, {
        sun_exposure: siteForm.sun || null,
        site_notes: siteForm.notes.trim() ? siteForm.notes.trim() : null,
      });
      setZone(r.zone);
      setSiteSheetOpen(false);
    } catch (e) {
      alert(e.message || 'Could not save');
    } finally {
      setSiteSaving(false);
    }
  };

  const plantOptions = useMemo(() => {
    const usedIds = new Set(markers.map((m) => m.garden_plant_id));
    return plants.map((p) => ({ ...p, alreadyPlaced: usedIds.has(p.id) }));
  }, [plants, markers]);

  if (loading) {
    return <div className="px-4 py-6 text-sm text-garden-muted">Loading…</div>;
  }
  if (!zone) {
    return (
      <div className="px-4 py-6">
        <div className="text-sm text-red-600 mb-3">{error || 'Area not found.'}</div>
        <PrimaryButton variant="secondary" onClick={() => navigate('/garden')}>
          <ArrowLeft size={16} /> Back to garden
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={quickPhotoInputRef}
        type="file"
        accept="image/*,image/heic,image/heif,.heic,.heif"
        capture="environment"
        className="hidden"
        onChange={handleQuickPhotoPicked}
      />
      <header className="flex items-center gap-2 px-2 pt-3 pb-2">
        <button
          onClick={() => navigate('/garden')}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">{zone.name}</h1>
        <button
          onClick={() => navigate(`/garden/ai?zone_id=${zone.id}`)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Ask AI about this zone"
          title="Ask AI"
        >
          <Sparkles size={20} />
        </button>
        <button
          onClick={() => navigate(`/garden/journal/new?zone_id=${zone.id}`)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Add journal entry for this zone"
          title="Journal this"
        >
          <BookOpen size={20} />
        </button>
        <button
          onClick={handleDeleteZone}
          className="min-h-11 min-w-11 -mr-2 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Delete area"
        >
          <Trash2 size={20} />
        </button>
      </header>

      <div className="px-4 pb-3 flex justify-center">
        <SeasonSwitcher
          current={currentSeason}
          onChange={setCurrentSeason}
          hasPhoto={hasPhoto}
        />
      </div>

      <div className="px-4 pb-3">
        <div className="rounded-2xl border border-garden-ink/10 bg-white/80 p-3 text-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {zone.sun_exposure || zone.site_notes ? (
                <div className="space-y-1.5">
                  {zone.sun_exposure ? (
                    <p className="text-garden-ink text-sm">
                      <span className="text-garden-muted">Sun: </span>
                      <span className="capitalize">{zone.sun_exposure}</span>
                    </p>
                  ) : null}
                  {zone.site_notes ? (
                    <p className="text-garden-ink text-sm whitespace-pre-wrap">{zone.site_notes}</p>
                  ) : null}
                </div>
              ) : (
                <p className="text-garden-muted text-sm">
                  Add sun and site notes to help AI suggest plants.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={openSiteEdit}
              className="text-garden-primary text-sm font-medium shrink-0"
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pb-3">
        <PrimaryButton
          onClick={() => {
            setPlacementEntryTab('menu');
            setPlacementEntryOpen(true);
          }}
          className="w-full"
          disabled={placementMode || !displayed.url || placementEntryOpen}
        >
          <Plus size={18} /> Add plant
        </PrimaryButton>
        {!displayed.url ? (
          <p className="mt-2 text-xs text-garden-muted text-center">
            Add a {currentSeason} photo below before placing plants.
          </p>
        ) : null}
      </div>

      <div
        ref={mapContainerRef}
        onClick={handleMapTap}
        className={
          `relative mx-4 rounded-2xl overflow-hidden border border-black/5 shadow-sm ` +
          `bg-garden-primaryLight ` +
          // When no photo is set we still need a frame to show the empty
          // state, so fall back to a portrait ratio. With a photo, we let
          // the image dictate the height so the whole picture is visible
          // (no cropping) and plant markers stay aligned pixel-for-pixel.
          (displayed.url ? '' : 'aspect-[3/4] ') +
          (placementMode ? 'cursor-crosshair' : '')
        }
        style={{ touchAction: draggingMarkerId ? 'none' : 'manipulation' }}
      >
        {displayed.url ? (
          <>
            <img
              src={displayed.url}
              alt={`${zone.name} in ${displayed.season}`}
              className="block w-full h-auto pointer-events-none select-none"
              draggable={false}
            />
            {displayed.season !== currentSeason ? (
              <div className="absolute top-2 left-2 px-2 py-0.5 text-[11px] font-medium bg-white/90 rounded-full text-garden-ink shadow-sm">
                Showing {displayed.season}
              </div>
            ) : null}
          </>
        ) : (
          <EmptySeasonState season={currentSeason} />
        )}

        {markers.map((m) => (
          <PlantMarker
            key={m.id}
            marker={m}
            onTap={(marker) => navigate(`/garden/plants/${marker.garden_plant_id}`)}
            onLongPressStart={onMarkerLongPressStart}
            onOptionsMenu={(marker) => setActiveMarker(marker)}
            isActive={activeMarker?.id === m.id}
            isDragging={draggingMarkerId === m.id}
          />
        ))}

        {placementMode ? <TapToPlaceOverlay onCancel={cancelPlacement} /> : null}
      </div>

      <div className="px-4 mt-4 pb-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,image/heic,image/heif,.heic,.heif"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleUploadPhoto(f);
            e.target.value = '';
          }}
        />
        <PrimaryButton
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
          disabled={uploading}
        >
          <Camera size={18} />
          {uploading
            ? 'Uploading…'
            : hasPhoto[currentSeason]
              ? `Change ${currentSeason} photo`
              : `Add ${currentSeason} photo`}
        </PrimaryButton>
      </div>

      {hasPhoto[currentSeason] ? (
        <div className="px-4 pb-4 -mt-2">
          <button
            onClick={handleRemoveSeasonPhoto}
            className="text-xs text-garden-muted underline underline-offset-2"
          >
            Remove {currentSeason} photo
          </button>
        </div>
      ) : null}

      <BottomSheet
        open={placementEntryOpen}
        onClose={() => {
          setPlacementEntryOpen(false);
          setPlacementEntryTab('menu');
        }}
        title="Add a plant to this area"
      >
        {placementEntryTab === 'menu' ? (
          <div className="space-y-3">
            <p className="text-sm text-garden-muted">
              Choose whether you are placing something already in your library or adding a new plant first, then tap the map.
            </p>
            <button
              type="button"
              onClick={() => setPlacementEntryTab('library')}
              className="w-full text-left rounded-2xl border border-black/5 bg-white shadow-sm p-4 flex items-center gap-3 min-h-[72px] active:scale-[0.99] transition hover:border-garden-primary/30"
            >
              <div className="w-12 h-12 rounded-2xl bg-garden-primaryLight text-garden-primaryDark flex items-center justify-center text-xl shrink-0" aria-hidden>
                📚
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-garden-ink">From saved plants</div>
                <div className="text-sm text-garden-muted">Pick one you already added to your library</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPlacementEntryTab('new')}
              className="w-full text-left rounded-2xl border border-black/5 bg-white shadow-sm p-4 flex items-center gap-3 min-h-[72px] active:scale-[0.99] transition hover:border-garden-primary/30"
            >
              <div className="w-12 h-12 rounded-2xl bg-garden-primaryLight text-garden-primaryDark flex items-center justify-center text-xl shrink-0" aria-hidden>
                ✨
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-garden-ink">Add new plant</div>
                <div className="text-sm text-garden-muted">Name, optional photo, then choose the spot</div>
              </div>
            </button>
          </div>
        ) : null}

        {placementEntryTab === 'library' ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setPlacementEntryTab('menu')}
              className="text-sm font-medium text-garden-primary"
            >
              ← Back
            </button>
            {plantOptions.length === 0 ? (
              <p className="text-sm text-garden-muted">You have no plants in your library yet. Use &ldquo;Add new plant&rdquo; instead.</p>
            ) : (
              <ul className="divide-y divide-black/5 max-h-[50vh] overflow-y-auto">
                {plantOptions.map((p) => {
                  const botSub = displayBotanicalSubtitle(p);
                  return (
                  <li key={p.id}>
                    <button
                      disabled={p.alreadyPlaced}
                      type="button"
                      onClick={() => {
                        if (p.alreadyPlaced) return;
                        setLibraryPlantIdForPlacement(p.id);
                        setPlacementEntryOpen(false);
                        setPlacementEntryTab('menu');
                        setPlacementMode(true);
                      }}
                      className="w-full text-left py-3 flex items-center justify-between min-h-11 disabled:opacity-40"
                    >
                      <div>
                        <div className="font-medium text-garden-ink">{displayPrimaryPlantName(p)}</div>
                        {botSub ? (
                          <div className="text-xs text-garden-muted italic">{botSub}</div>
                        ) : null}
                      </div>
                      <span className="text-xs text-garden-muted">{p.alreadyPlaced ? 'already here' : 'Choose'}</span>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
            {plantOptions.some((p) => !p.alreadyPlaced) ? (
              <p className="text-xs text-garden-muted">After you close this list, tap the map where the plant should go.</p>
            ) : null}
          </div>
        ) : null}

        {placementEntryTab === 'new' ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setPlacementEntryTab('menu')}
              className="text-sm font-medium text-garden-primary"
            >
              ← Back
            </button>
            <p className="text-sm text-garden-muted">
              Enter a name (and optional photo), then tap the map to drop the pin.
            </p>
            <div className="flex gap-3 items-start">
              <button
                type="button"
                onClick={() => quickPhotoInputRef.current?.click()}
                className="relative w-20 h-20 rounded-2xl bg-garden-primaryLight border border-garden-ink/10 flex items-center justify-center shrink-0 overflow-hidden"
                aria-label={quickPhotoPreview ? 'Change photo' : 'Add photo'}
              >
                {quickPhotoPreview ? (
                  <img src={quickPhotoPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-garden-muted">
                    <Camera size={20} />
                    <span className="text-[10px] mt-0.5">Photo</span>
                  </div>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex rounded-full border border-garden-ink/15 p-0.5 text-[11px] font-medium mb-2">
                  <button
                    type="button"
                    onClick={() => setQuickNameMode('common')}
                    className={
                      'flex-1 min-h-8 rounded-full py-1.5 transition ' +
                      (quickNameMode === 'common'
                        ? 'bg-garden-primary text-white'
                        : 'text-garden-muted')
                    }
                  >
                    Common name
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickNameMode('scientific')}
                    className={
                      'flex-1 min-h-8 rounded-full py-1.5 transition ' +
                      (quickNameMode === 'scientific'
                        ? 'bg-garden-primary text-white'
                        : 'text-garden-muted')
                    }
                  >
                    Scientific
                  </button>
                </div>
                <input
                  value={newPlantName}
                  onChange={(e) => {
                    setNewPlantName(e.target.value);
                    setQuickIdentifyMessage(null);
                  }}
                  placeholder={quickNameMode === 'common' ? 'e.g. Roses' : 'e.g. Rosa rubiginosa'}
                  className="w-full min-h-11 px-4 rounded-full border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-base"
                />
              </div>
            </div>
            {quickPhotoFile && !newPlantName.trim() ? (
              <div className="rounded-xl border border-garden-ink/10 bg-garden-primaryLight/20 px-3 py-2 space-y-2">
                <p className="text-[11px] text-garden-ink font-medium">Suggest a name from your photo</p>
                <select
                  value={quickIdentifyOrgan}
                  onChange={(e) => setQuickIdentifyOrgan(e.target.value)}
                  disabled={quickIdentifyBusy}
                  className="w-full min-h-9 rounded-lg border border-garden-ink/15 px-2 text-sm bg-white"
                >
                  {PLANT_IDENTIFY_ORGANS.map((o) => (
                    <option key={o} value={o}>
                      {o.charAt(0).toUpperCase() + o.slice(1)} in photo
                    </option>
                  ))}
                </select>
                <PrimaryButton
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={quickIdentifyBusy}
                  onClick={suggestNameFromPhoto}
                >
                  {quickIdentifyBusy ? 'Identifying\u2026' : 'Suggest name from photo'}
                </PrimaryButton>
                <p className="text-[10px] text-garden-muted">
                  Uses PlantNet (daily limit per account). HEIC photos are supported.
                </p>
              </div>
            ) : null}
            {quickIdentifyMessage ? (
              <p className="text-[11px] text-garden-ink/80">{quickIdentifyMessage}</p>
            ) : null}
            <PrimaryButton
              className="w-full"
              disabled={!newPlantName.trim() || quickAddBusy}
              onClick={() => {
                setPlacementEntryOpen(false);
                setPlacementEntryTab('menu');
                setMapPlacementFromPreForm(true);
                setPlacementMode(true);
              }}
            >
              Choose spot on map
            </PrimaryButton>
          </div>
        ) : null}
      </BottomSheet>

      <BottomSheet
        open={choosePlantOpen}
        onClose={() => {
          setChoosePlantOpen(false);
          setPendingPoint(null);
          setMapPlacementFromPreForm(false);
        }}
        title={mapPlacementFromPreForm ? 'Place plant here?' : 'Pick a plant for this spot'}
      >
        <div className="space-y-4">
          {mapPlacementFromPreForm ? (
            <div className="space-y-3">
              <div className="flex gap-3 items-start">
                {quickPhotoPreview ? (
                  <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border border-garden-ink/10">
                    <img src={quickPhotoPreview} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : null}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-garden-ink">{newPlantName.trim() || 'New plant'}</p>
                  <button
                    type="button"
                    className="mt-1 text-xs text-garden-primary font-medium"
                    onClick={() => setMapPlacementFromPreForm(false)}
                  >
                    Edit name &amp; photo
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <PrimaryButton
                  variant="secondary"
                  onClick={() => createPlantAndPlace({ withAI: false })}
                  disabled={!newPlantName.trim() || quickAddBusy}
                  className="flex-1"
                >
                  {quickAddBusy ? 'Adding\u2026' : 'Just add'}
                </PrimaryButton>
                <PrimaryButton
                  onClick={() => createPlantAndPlace({ withAI: true })}
                  disabled={!newPlantName.trim() || quickAddBusy}
                  className="flex-1"
                >
                  <Sparkles size={16} /> Add + AI fill
                </PrimaryButton>
              </div>
            </div>
          ) : null}

          {!mapPlacementFromPreForm ? (
          <>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-garden-muted mb-2">
              Quick add
            </div>

            <div className="flex gap-3 items-start">
              <button
                type="button"
                onClick={() => quickPhotoInputRef.current?.click()}
                className="relative w-20 h-20 rounded-2xl bg-garden-primaryLight border border-garden-ink/10 flex items-center justify-center shrink-0 overflow-hidden"
                aria-label={quickPhotoPreview ? 'Change photo' : 'Add photo'}
              >
                {quickPhotoPreview ? (
                  <img src={quickPhotoPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center text-garden-muted">
                    <Camera size={20} />
                    <span className="text-[10px] mt-0.5">Photo</span>
                  </div>
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex rounded-full border border-garden-ink/15 p-0.5 text-[11px] font-medium mb-2">
                  <button
                    type="button"
                    onClick={() => setQuickNameMode('common')}
                    className={
                      'flex-1 min-h-8 rounded-full py-1.5 transition ' +
                      (quickNameMode === 'common'
                        ? 'bg-garden-primary text-white'
                        : 'text-garden-muted')
                    }
                  >
                    Common name
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickNameMode('scientific')}
                    className={
                      'flex-1 min-h-8 rounded-full py-1.5 transition ' +
                      (quickNameMode === 'scientific'
                        ? 'bg-garden-primary text-white'
                        : 'text-garden-muted')
                    }
                  >
                    Scientific
                  </button>
                </div>
                <input
                  value={newPlantName}
                  onChange={(e) => {
                    setNewPlantName(e.target.value);
                    setQuickIdentifyMessage(null);
                  }}
                  placeholder={quickNameMode === 'common' ? 'e.g. Roses' : 'e.g. Rosa rubiginosa'}
                  className="w-full min-h-11 px-4 rounded-full border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-base"
                />
                <p className="mt-1 text-[11px] text-garden-muted leading-snug">
                  {quickNameMode === 'common'
                    ? 'Stored as the common name. Add + AI fill can suggest botanical name and care.'
                    : 'We run a quick lookup on the Latin name, then save both names when possible.'}
                </p>
                {quickPhotoFile ? (
                  <button
                    type="button"
                    onClick={clearQuickPhoto}
                    className="mt-1 text-[11px] text-garden-muted underline underline-offset-2"
                  >
                    Remove photo
                  </button>
                ) : (
                  <p className="mt-1 text-[11px] text-garden-muted">
                    Photo is optional {'\u2014'} tap the camera to snap one.
                  </p>
                )}
              </div>
            </div>

            {quickPhotoFile && !newPlantName.trim() ? (
              <div className="rounded-xl border border-garden-ink/10 bg-garden-primaryLight/20 px-3 py-2 space-y-2">
                <p className="text-[11px] text-garden-ink font-medium">Suggest a name from your photo</p>
                <select
                  value={quickIdentifyOrgan}
                  onChange={(e) => setQuickIdentifyOrgan(e.target.value)}
                  disabled={quickIdentifyBusy}
                  className="w-full min-h-9 rounded-lg border border-garden-ink/15 px-2 text-sm bg-white"
                >
                  {PLANT_IDENTIFY_ORGANS.map((o) => (
                    <option key={o} value={o}>
                      {o.charAt(0).toUpperCase() + o.slice(1)} in photo
                    </option>
                  ))}
                </select>
                <PrimaryButton
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={quickIdentifyBusy}
                  onClick={suggestNameFromPhoto}
                >
                  {quickIdentifyBusy ? 'Identifying\u2026' : 'Suggest name from photo'}
                </PrimaryButton>
                <p className="text-[10px] text-garden-muted">
                  Uses PlantNet (daily limit per account). HEIC photos are supported.
                </p>
              </div>
            ) : null}
            {quickIdentifyMessage ? (
              <p className="text-[11px] text-garden-ink/80">{quickIdentifyMessage}</p>
            ) : null}

            <div className="mt-3 flex gap-2">
              <PrimaryButton
                variant="secondary"
                onClick={() => createPlantAndPlace({ withAI: false })}
                disabled={!newPlantName.trim() || quickAddBusy}
                className="flex-1"
              >
                {quickAddBusy ? 'Adding\u2026' : 'Just add'}
              </PrimaryButton>
              <PrimaryButton
                onClick={() => createPlantAndPlace({ withAI: true })}
                disabled={!newPlantName.trim() || quickAddBusy}
                className="flex-1"
              >
                <Sparkles size={16} /> Add + AI fill
              </PrimaryButton>
            </div>
            <p className="mt-1 text-[11px] text-garden-muted">
              &ldquo;AI fill&rdquo; looks up typical care details so you don&rsquo;t start from scratch. You can edit afterward.
            </p>
          </div>

          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-garden-muted mb-2">
              From your library
            </div>
            {plantOptions.length === 0 ? (
              <div className="text-sm text-garden-muted">
                You haven't added any plants yet. Type a name above to create one.
              </div>
            ) : (
              <ul className="divide-y divide-black/5">
                {plantOptions.map((p) => {
                  const botSub = displayBotanicalSubtitle(p);
                  return (
                  <li key={p.id}>
                    <button
                      disabled={p.alreadyPlaced}
                      onClick={() => placeMarkerForPlant(p.id)}
                      className="w-full text-left py-3 flex items-center justify-between min-h-11 disabled:opacity-40"
                    >
                      <div>
                        <div className="font-medium text-garden-ink">{displayPrimaryPlantName(p)}</div>
                        {botSub ? (
                          <div className="text-xs text-garden-muted italic">{botSub}</div>
                        ) : null}
                      </div>
                      <span className="text-xs text-garden-muted">
                        {p.alreadyPlaced ? 'placed here' : 'place'}
                      </span>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
          </>
          ) : null}
        </div>
      </BottomSheet>

      <BottomSheet
        open={siteSheetOpen}
        onClose={() => !siteSaving && setSiteSheetOpen(false)}
        title="Area sun and site"
      >
        <div className="space-y-3">
          <p className="text-sm text-garden-muted">
            This helps the assistant match plants to real conditions in this part of your yard.
          </p>
          <div>
            <label className="block text-xs font-medium text-garden-muted mb-1.5">Sun / exposure</label>
            <select
              value={siteForm.sun}
              onChange={(e) => setSiteForm((f) => ({ ...f, sun: e.target.value }))}
              className="w-full min-h-12 px-4 rounded-xl border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-sm bg-white capitalize"
            >
              <option value="">Not sure yet</option>
              {GARDEN_SUN_EXPOSURE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-garden-muted mb-1.5">Site notes (optional)</label>
            <textarea
              value={siteForm.notes}
              onChange={(e) => setSiteForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="e.g. Fenced — deer don’t get in; stays wet in spring"
              className="w-full px-4 py-3 rounded-xl border border-garden-ink/15 focus:outline-none focus:border-garden-primary text-sm resize-y min-h-[4.5rem]"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <PrimaryButton
              variant="secondary"
              className="flex-1"
              onClick={() => setSiteSheetOpen(false)}
              disabled={siteSaving}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              className="flex-1"
              onClick={saveSiteDetails}
              disabled={siteSaving}
            >
              {siteSaving ? 'Saving…' : 'Save'}
            </PrimaryButton>
          </div>
        </div>
      </BottomSheet>

      <PlantBottomSheet
        open={!!activeMarker}
        onClose={() => setActiveMarker(null)}
        marker={activeMarker}
        onReposition={() => {
          alert('Long-press the marker on the map and drag to reposition.');
          setActiveMarker(null);
        }}
        onRemoveFromMap={removeMarkerFromMap}
      />

      {toast ? (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 max-w-[360px] w-[calc(100%-24px)] px-3">
          <div className="rounded-2xl bg-garden-ink text-white shadow-lg px-3 py-2.5 flex items-center gap-2">
            <Sparkles size={16} className="text-garden-accent shrink-0" />
            <div className="flex-1 text-sm min-w-0">
              <div className="font-medium truncate">&ldquo;{toast.plantName}&rdquo; added</div>
              <div className="text-xs text-white/70 truncate">
                {toast.aiError
                  ? toast.aiError
                  : toast.aiFilled
                    ? `AI filled ${toast.aiFilled} detail${toast.aiFilled === 1 ? '' : 's'}${toast.photoUploaded ? ' \u00b7 photo saved' : ''}`
                    : toast.photoUploaded
                      ? 'Photo saved \u00b7 add care details anytime'
                      : 'Add care details anytime'}
              </div>
            </div>
            <button
              onClick={() => {
                const id = toast.plantId;
                setToast(null);
                navigate(`/garden/plants/${id}/edit`);
              }}
              className="px-3 py-1.5 rounded-full bg-white text-garden-ink text-xs font-semibold shrink-0"
            >
              Edit
            </button>
            <button
              onClick={() => setToast(null)}
              className="p-1 text-white/60 hover:text-white shrink-0"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EmptySeasonState({ season }) {
  const emoji = season === 'summer' ? '☀️' : season === 'fall' ? '🍂' : season === 'winter' ? '❄️' : '🌸';
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-garden-primaryDark">
      <div className="text-6xl mb-3" aria-hidden>{emoji}</div>
      <div className="text-base font-semibold capitalize">No {season} photo yet</div>
      <div className="text-sm text-garden-muted mt-1 max-w-[260px]">
        Snap a photo of this area in {season} to start placing plants on it.
      </div>
    </div>
  );
}
